import { escapeHtml } from "./format.js";

// Shared open/close wiring for a "More" dropdown menu — a trigger button that
// toggles an anchored popover menu. Used by the invoice actions menu
// (invoice-status-drawer.js) and the recurring schedule detail menu
// (schedules.js); both render the menu as an `.app-account-menu` popover
// anchored under a secondary "More" button.
//
// Behaviour (kept identical to the two call sites it replaced):
//   - Click the trigger to toggle the menu open/closed.
//   - While open, an outside click or Escape closes it (Escape also refocuses
//     the trigger). Clicking a menu item closes it too — the item's own
//     handler, wired separately by id, still runs.
//   - The document-level outside-click / Escape listeners are attached on a
//     deferred tick so the opening click doesn't immediately close the menu,
//     and torn down again on close.
//   - `aria-expanded` on the trigger tracks the open state.
//
// Flips the menu to the opposite vertical side of its trigger when its
// CSS-authored default direction (down for filter/action menus, up for the
// sidebar/topbar account menu — see each component's `top`/`bottom` rule)
// doesn't have room to render in full, but the opposite side does. Falls
// back to the default direction (and lets the page scroll) if neither side
// has room, rather than guaranteeing a cut-off menu on both. Inline styles
// win over every per-context CSS override without needing `!important`.
function flipIfNeeded(triggerBtn, menuEl) {
  menuEl.style.top = "";
  menuEl.style.bottom = "";
  const opensDown = getComputedStyle(menuEl).top !== "auto";
  const triggerRect = triggerBtn.getBoundingClientRect();
  const menuHeight = menuEl.getBoundingClientRect().height;
  const gap = 8;
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  if (opensDown && menuHeight + gap > spaceBelow && spaceAbove >= menuHeight + gap) {
    menuEl.style.top = "auto";
    menuEl.style.bottom = `calc(100% + ${gap}px)`;
  } else if (!opensDown && menuHeight + gap > spaceAbove && spaceBelow >= menuHeight + gap) {
    menuEl.style.bottom = "auto";
    menuEl.style.top = `calc(100% + ${gap}px)`;
  }
}

// Which side of its trigger a menu should hang from (live sweep, 31 October
// 2026). Each menu's CSS anchors one edge (`left: 0`, growing right, for the
// invoice drawer's More), chosen for the width it was drawn at; between 768 and
// 1023 the drawer is the whole window and More sits two thirds across, so the
// 226px menu ran past the drawer's own edge and was cut off at Void and
// Replace. When the side it opens towards has no room and the other does, it
// hangs from the other edge. `bounds` is the box it has to stay inside: the
// nearest ancestor that clips it, or the window. Pure, for
// test/dropdown-menu.test.mjs. "start" keeps the CSS's side; "end" flips it.
export function horizontalSide({ triggerLeft, triggerRight, menuWidth, boundsLeft, boundsRight, opensRight }) {
  const roomRight = boundsRight - triggerLeft;
  const roomLeft = triggerRight - boundsLeft;
  if (opensRight) return menuWidth > roomRight && menuWidth <= roomLeft ? "end" : "start";
  return menuWidth > roomLeft && menuWidth <= roomRight ? "end" : "start";
}

// At the narrowest widths a menu fits neither side of its trigger, though it
// fits in its box: at 360 More's menu ran 8px past the drawer. The pixels that
// bring it inside, 0 when it is inside or wider than the box. Pure, as above.
export function sidewaysNudge({ menuLeft, menuRight, boundsLeft, boundsRight }) {
  if (menuRight - menuLeft > boundsRight - boundsLeft) return 0;
  if (menuRight > boundsRight) return boundsRight - menuRight;
  if (menuLeft < boundsLeft) return boundsLeft - menuLeft;
  return 0;
}

// The box a menu must stay inside: the nearest ancestor that clips its
// overflow, within the window.
function clippingBounds(menuEl) {
  let left = 0;
  let right = window.innerWidth;
  for (let el = menuEl.parentElement; el && el !== document.body; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowX !== "visible" || style.overflow !== "visible") {
      const rect = el.getBoundingClientRect();
      left = Math.max(left, rect.left);
      right = Math.min(right, rect.right);
      break;
    }
  }
  return { left, right };
}

function placeSideways(triggerBtn, menuEl) {
  menuEl.style.left = "";
  menuEl.style.right = "";
  menuEl.style.translate = "";
  const trigger = triggerBtn.getBoundingClientRect();
  const menu = menuEl.getBoundingClientRect();
  // Which edge the CSS hangs it from, read off where it landed: a computed
  // `left` is a length even when the rule says auto.
  const opensRight = Math.abs(menu.left - trigger.left) <= Math.abs(menu.right - trigger.right);
  const bounds = clippingBounds(menuEl);
  const side = horizontalSide({
    triggerLeft: trigger.left,
    triggerRight: trigger.right,
    menuWidth: menu.width,
    boundsLeft: bounds.left,
    boundsRight: bounds.right,
    opensRight
  });
  if (side === "end") {
    if (opensRight) { menuEl.style.left = "auto"; menuEl.style.right = "0"; }
    else { menuEl.style.right = "auto"; menuEl.style.left = "0"; }
  }
  const placed = menuEl.getBoundingClientRect();
  const nudge = sidewaysNudge({ menuLeft: placed.left, menuRight: placed.right, boundsLeft: bounds.left, boundsRight: bounds.right });
  if (nudge) menuEl.style.translate = `${nudge}px`;
}

// A menu row that picks one value out of a set — which company you're in,
// what role someone has — rather than performing an action.
//
// Two things make it a single-choice row instead of a plain `menuitem`:
// `role="menuitemradio"` + `aria-checked` (so a screen reader announces the
// set AND which member of it is current), and a leading checkmark. Rows that
// aren't current get an empty box of the same width, so the labels stay on
// one vertical line instead of shifting as the selection moves.
//
// Shared because the two menus that need it drifted: the company switcher
// drew its own checkmark, while the team role menu showed no current value at
// all — you opened "Change role" on an admin and got three identical rows.
const MENU_CHECK_HTML = `<img class="app-account-menu-icon" src="/src/assets/icons/Icon=checkmark.svg" alt="" aria-hidden="true" />`;
const MENU_CHECK_SPACER_HTML = `<span class="app-account-menu-icon" aria-hidden="true"></span>`;

export function menuRadioItemHtml({ label, checked, id = "", attrs = "" }) {
  return `
    <button
      class="app-account-menu-item"
      type="button"
      role="menuitemradio"
      aria-checked="${checked ? "true" : "false"}"
      ${id ? `id="${escapeHtml(id)}"` : ""}
      ${attrs}
    >
      ${checked ? MENU_CHECK_HTML : MENU_CHECK_SPACER_HTML}
      <span class="app-account-menu-label">${escapeHtml(label)}</span>
    </button>`;
}

// `onItemClick` is an optional callback fired (after the menu closes) when a
// menu item is clicked, receiving the click event. No-ops safely if either
// element is missing.
export function wireDropdownMenu(triggerBtn, menuEl, { onItemClick } = {}) {
  if (!triggerBtn || !menuEl) return;

  function onOutsideClick(evt) {
    if (!menuEl.contains(evt.target) && !triggerBtn.contains(evt.target)) close();
  }
  function onEsc(evt) {
    if (evt.key === "Escape") { close(); triggerBtn.focus(); }
  }
  function close() {
    menuEl.hidden = true;
    triggerBtn.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", onOutsideClick);
    document.removeEventListener("keydown", onEsc);
  }

  triggerBtn.addEventListener("click", (evt) => {
    evt.stopPropagation();
    if (!menuEl.hidden) { close(); return; }
    menuEl.hidden = false;
    triggerBtn.setAttribute("aria-expanded", "true");
    flipIfNeeded(triggerBtn, menuEl);
    placeSideways(triggerBtn, menuEl);
    // Defer so this opening click doesn't immediately trip the outside handler.
    setTimeout(() => {
      document.addEventListener("click", onOutsideClick);
      document.addEventListener("keydown", onEsc);
    }, 0);
  });

  // Close after any item-click; the action's own id-wired handler still runs.
  menuEl.addEventListener("click", (evt) => {
    if (evt.target.closest(".app-account-menu-item")) {
      close();
      onItemClick?.(evt);
    }
  });
}
